/**
 * copy.ts — the ONLY place the words a customer reads about a connector's
 * state or a failure live (D-19). The server sends codes, never sentences we
 * show: each code maps to a userKey (server/lib/connectorContract.js,
 * mirrored into connectorCodes.json by scripts/build-connector-codes.js) and
 * each userKey has one line here.
 *
 * Audience: non-technical. Never write OAuth, token, API key, MCP, endpoint
 * or client id (copy.test.ts scans every string below). `{service}` is
 * replaced with the connector's name ("Stripe"), or "This service" when the
 * page does not know which one; keep it at the start of a sentence so both
 * read naturally.
 *
 * Unknown codes and statuses get GENERIC_COPY, never an empty row (DX-17).
 */
import codes from './connectorCodes.json';

type CodeTable = Record<string, { userKey: string; retryable: boolean }>;
const CODE_TABLE: CodeTable = codes.codes as CodeTable;

export const GENERIC_COPY = "{service} can't be connected right now. Try again later.";

/** One line per server userKey (connectorContract.js ERROR_CODES[*].userKey). */
export const USER_COPY: Record<string, string> = {
  needs_reauth: '{service} needs you to reconnect it before your AI coworker can use it again.',
  unavailable: "{service} isn't available right now. Try again in a few minutes.",
  registration_refused: GENERIC_COPY,
  cancelled: 'You cancelled connecting {service}. Nothing was changed.',
  read_only: '{service} is read-only, so your AI coworker asks before changing anything.',
  approval_denied: 'You declined that {service} change. Nothing was changed.',
  disconnected: '{service} is disconnected.',
  link_expired: 'That link expired, so nothing was connected. Try connecting again.',
  needs_setup: '{service} needs a little more setup. Connect it again to finish.',
  rate_limited: 'Too many tries. Wait a minute and try again.',
};

// Codes the connect/complete routes answer with that are not error codes of
// the contract (server/routes/connectors.js PROBLEM_COPY) → a userKey above,
// or GENERIC_COPY when absent.
const FLOW_CODE_KEYS: Record<string, string> = {
  rate_limited: 'rate_limited',
  link_expired: 'link_expired',
  vendor_unavailable: 'unavailable',
  registration_refused: 'registration_refused',
};
const FLOW_COPY: Record<string, string> = {
  not_found: "{service} isn't available on your account.",
  github_not_linked: 'Connect GitHub on your Profile page first, then connect it here.',
  use_enable: '{service} is turned on with Enable, not Connect.',
  invalid_request: 'Something went wrong. Try again.',
  internal: 'Something went wrong on our side. Try again.',
};

export const REAUTHORIZE_COPY = 'One more step. Taking you back to approve the connection…';
export const COMPLETING_COPY = 'Finishing your connection…';
export const BACK_TO_CONNECTIONS = 'Back to connections';
export const READ_ONLY_LABEL = 'Read-only';
export const READ_ONLY_HELP = "Your AI coworker can look things up but can't make changes.";
// Off: every change still waits for the user's yes on an approval card
// (server/services/connectorBuildBridge ask()).
export const READ_ONLY_OFF_HELP = 'Your AI coworker can make changes, and asks you first.';
export const READ_ONLY_TIMING = 'Takes effect on your next message.';
// Stripe (readOnlyUnlessTestMode) outside test mode: the server refuses to
// turn read-only off, so the switch says why instead of failing.
export const READ_ONLY_LOCKED_TEST_MODE = (name: string) => `Stays on for a ${name} account outside test mode. To let your AI coworker make changes, connect a test-mode account.`;
export const READ_ONLY_SAVE_FAILED = "Couldn't save that change. Try again.";
export const TEST_MODE_LABEL = 'Test mode';
export const LIVE_MODE_LABEL = 'Live mode';
export const LIVE_MODE_HELP = 'This is your live account with real customers and money.';
export const ACCOUNT_SWITCHED_COPY = 'This is a different account from last time, so its settings start fresh.';
export const DISCONNECT_CONFIRM = "Disconnect {service}? Your AI coworker loses access until you connect it again.";
export const DISCONNECTED_COPY = '{service} is disconnected.';
export const DISCONNECT_FAILED = "Couldn't disconnect {service}. Try again.";
export const NEEDS_RECONNECT_PILL = 'Needs reconnecting';

// D-13: one line under the name for connectors that look alike side by side.
export const USE_FOR_LABELS: Record<string, string> = {
  resend: 'Emails your app sends',
  klaviyo: 'Marketing lists and campaigns',
  calcom: 'Booking pages you run',
  calendly: 'Your Calendly booking links',
};

/** Fill `{service}`; a missing name reads "This service" (or "this service" mid-sentence). */
export function fill(text: string, serviceName?: string | null): string {
  if (serviceName) return text.split('{service}').join(serviceName);
  return text
    .replace(/(^|[.!?]\s+)\{service\}/g, '$1This service')
    .split('{service}')
    .join('this service');
}

export function userKeyForCode(code: string | null | undefined): string | null {
  if (!code) return null;
  if (CODE_TABLE[code]) return CODE_TABLE[code].userKey;
  return FLOW_CODE_KEYS[code] || null;
}

/** The line for a server code (contract code, flow code or anything else). */
export function messageForCode(code: string | null | undefined, serviceName?: string | null): string {
  const key = userKeyForCode(code);
  if (key && USER_COPY[key]) return fill(USER_COPY[key], serviceName);
  if (code && FLOW_COPY[code]) return fill(FLOW_COPY[code], serviceName);
  return fill(GENERIC_COPY, serviceName);
}

/** The line under a connection whose status is not plain connected/not connected. */
export function messageForStatus(status: string | null | undefined, serviceName?: string | null): string {
  switch (status) {
    case 'connected':
      return fill('{service} is connected.', serviceName);
    case 'needs_reauth':
      return fill(USER_COPY.needs_reauth, serviceName);
    case 'needs_setup':
      return fill(USER_COPY.needs_setup, serviceName);
    case 'disconnected':
      return fill(USER_COPY.disconnected, serviceName);
    case 'error':
      return fill(USER_COPY.unavailable, serviceName);
    default:
      return fill(GENERIC_COPY, serviceName);
  }
}

/** The one line shown after coming back from the service's own page. */
/**
 * The account name to show, or null. Without a vendor "who am I" the server
 * stores the service's own name as the label (connectorOAuth), which would
 * read "Connected as PostHog".
 */
export function realAccountLabel(label: string | null | undefined, serviceName?: string | null): string | null {
  // The server stores "<service> · <mode> · <account>" (lib/connectorMcpClient
  // lookupAccount); the panel shows the name and a mode badge already, so only
  // the account part is the account.
  const svc = (serviceName || '').trim().toLowerCase();
  const parts = (label || '').split(' · ').map((p) => p.trim());
  if (svc && parts.length && parts[0].toLowerCase() === svc) parts.shift();
  if (parts.length && /^(test|live) mode$/i.test(parts[0])) parts.shift();
  const l = parts.join(' · ').trim();
  if (!l) return null;
  if (svc && l.toLowerCase() === svc) return null;
  return l;
}

export function resultLine({
  result,
  serviceName,
  accountLabel,
  reason,
}: {
  result: string;
  serviceName?: string | null;
  accountLabel?: string | null;
  reason?: string | null;
}): string {
  if (result === 'connected') {
    const label = realAccountLabel(accountLabel, serviceName);
    return label ? `Connected as ${label}.` : fill('{service} is connected.', serviceName);
  }
  if (result === 'cancelled') return fill(USER_COPY.cancelled, serviceName);
  if (result === 'error') return messageForCode(reason || null, serviceName);
  return fill(GENERIC_COPY, serviceName);
}

export function retryInCopy(seconds: number): string {
  const n = Math.max(1, Math.ceil(seconds));
  return `Too many tries. Try again in ${n} second${n === 1 ? '' : 's'}.`;
}

/** Every string above, for the banned-words test. */
export const ALL_CONNECTOR_COPY: string[] = [
  GENERIC_COPY,
  ...Object.values(USER_COPY),
  ...Object.values(FLOW_COPY),
  REAUTHORIZE_COPY,
  COMPLETING_COPY,
  BACK_TO_CONNECTIONS,
  READ_ONLY_LABEL,
  READ_ONLY_HELP,
  READ_ONLY_OFF_HELP,
  READ_ONLY_TIMING,
  READ_ONLY_SAVE_FAILED,
  TEST_MODE_LABEL,
  LIVE_MODE_LABEL,
  LIVE_MODE_HELP,
  ACCOUNT_SWITCHED_COPY,
  DISCONNECT_CONFIRM,
  DISCONNECTED_COPY,
  DISCONNECT_FAILED,
  NEEDS_RECONNECT_PILL,
  ...Object.values(USE_FOR_LABELS),
  retryInCopy(30),
  messageForStatus('connected'),
];
